// VfxInstance — composition layer. Given a VfxDef + world position, mounts
// the appropriate primitive components (Particles / Beam / Ring / Shake)
// and fires onComplete after durationSec.
//
// One VfxInstance per active effect; VfxLayer manages the pool.

import { useEffect } from 'react';
import { Beam } from './vfx/Beam.js';
import { Particles } from './vfx/Particles.js';
import { Ring } from './vfx/Ring.js';
import { Shake } from './vfx/Shake.js';
import type { VfxDef } from './vfx/types.js';

interface VfxInstanceProps {
  readonly def: VfxDef;
  readonly position: readonly [number, number, number];
  readonly beamTargetOverride?: readonly [number, number, number];
  readonly onComplete?: () => void;
}

export function VfxInstance({ def, position, beamTargetOverride, onComplete }: VfxInstanceProps) {
  useEffect(() => {
    const t = setTimeout(() => onComplete?.(), def.durationSec * 1000);
    return () => clearTimeout(t);
  }, [def.durationSec, onComplete]);

  return (
    <>
      {def.particles && <Particles def={def.particles} position={position} />}
      {def.beam && <Beam def={def.beam} position={position} targetOverride={beamTargetOverride} />}
      {def.ring && <Ring def={def.ring} position={position} />}
      {def.shake && <Shake def={def.shake} />}
    </>
  );
}
